import { useCallback } from 'react';

import { PUBLISH_ERROR_TYPE } from 'app/components/commons/PublishModalError/PublishModalError';
import { usePublishExperience } from 'app/hooks/data/core/admin/experiences/usePublishExperience';
import { useUnpublishExperience } from 'app/hooks/data/core/admin/experiences/useUnpublishExperience';
import { usePublishOldExperience } from 'app/hooks/data/useExperience';

import type { HotelActivity } from '../_types';

type Props = {
  hotelId: number;
  refetchActivities: () => Promise<unknown>;
  openPublishErrorModal: (error: unknown, type: PUBLISH_ERROR_TYPE) => void;
  isLegacy: boolean;
};

export const useActivityActions = ({
  hotelId,
  refetchActivities,
  openPublishErrorModal,
  isLegacy,
}: Props) => {
  const { mutateAsync: publishLegacyExperience } = usePublishOldExperience();
  const { mutateAsync: publishExperience } = usePublishExperience();
  const { mutateAsync: unpublishExperience } = useUnpublishExperience();

  const publishActivity = useCallback(
    async (activity: HotelActivity) => {
      try {
        if (isLegacy === true) {
          await publishLegacyExperience(activity.id);
        } else {
          await publishExperience({ experienceId: activity.id, hotelId });
        }

        await refetchActivities();
      } catch (error) {
        openPublishErrorModal(error, PUBLISH_ERROR_TYPE.ACTIVITY);
      }
    },
    [
      hotelId,
      isLegacy,
      openPublishErrorModal,
      publishExperience,
      publishLegacyExperience,
      refetchActivities,
    ]
  );

  const unpublishActivity = useCallback(
    async (activity: HotelActivity) => {
      try {
        if (isLegacy) {
          await publishLegacyExperience(activity.id);
        } else {
          await unpublishExperience({ experienceId: activity.id, hotelId });
        }

        await refetchActivities();
      } catch (error) {
        openPublishErrorModal(error, PUBLISH_ERROR_TYPE.ACTIVITY);
      }
    },
    [
      hotelId,
      isLegacy,
      openPublishErrorModal,
      publishLegacyExperience,
      refetchActivities,
      unpublishExperience,
    ]
  );

  return { publishActivity, unpublishActivity };
};
